import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, DollarSign, Search, Filter, X, Heart, ArrowRight, Gift, Target, TrendingUp } from 'lucide-react';
import SEOHead from '../SEO/SEOHead';

// Get the API URL from the environment or use the default
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3011/api';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [ngoSearchTerm, setNgoSearchTerm] = useState('');
  const [ngoSuggestions, setNgoSuggestions] = useState([]);
  const [selectedNgo, setSelectedNgo] = useState(null);
  const [showNgoSuggestions, setShowNgoSuggestions] = useState(false);
  const [loadingNgos, setLoadingNgos] = useState(false);
  const ngoSearchRef = useRef(null);
  const [filters, setFilters] = useState({
    category: '',
    sortBy: 'newest',
    ngoId: ''
  });

  // Structured data for NGO projects page
  const projectsStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "NGO Blood Donation Projects",
    "description": "Browse and support various NGO blood donation projects and campaigns across India",
    "url": "https://www.thebloodhero.com/projects",
    "mainEntity": {
      "@type": "Organization",
      "name": "BloodHero",
      "url": "https://www.thebloodhero.com"
    },
    "itemListElement": projects.map((project, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "CreativeWork",
        "name": project.title || project.name,
        "description": project.description,
        "url": `https://www.thebloodhero.com/projects/${project._id}`,
        "creator": {
          "@type": "Organization",
          "name": project.ngo?.name || "NGO"
        },
        "dateCreated": project.createdAt,
        "genre": project.category || "Blood Donation"
      }
    }))
  };

  // Click outside handler for NGO suggestions dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ngoSearchRef.current && !ngoSearchRef.current.contains(event.target)) {
        setShowNgoSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/public/projects/active`);
        setProjects(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError('Failed to load projects');
        setLoading(false);
        toast.error('Failed to load projects');
      }
    };

    fetchProjects();
  }, []);
  
  // Search NGOs when ngoSearchTerm changes
  useEffect(() => {
    const searchNGOs = async () => {
      if (!ngoSearchTerm.trim()) {
        setNgoSuggestions([]);
        setShowNgoSuggestions(false);
        return;
      }
      
      try {
        setLoadingNgos(true);
        const response = await axios.get(`${API_URL}/ngos/search?query=${encodeURIComponent(ngoSearchTerm)}`);
        setNgoSuggestions(response.data);
        setShowNgoSuggestions(true);
        setLoadingNgos(false);
      } catch (err) {
        console.error('Error searching NGOs:', err);
        setNgoSuggestions([]);
        setLoadingNgos(false);
      }
    };
    
    // Debounce search to avoid too many requests
    const timeoutId = setTimeout(() => {
      if (ngoSearchTerm) {
        searchNGOs();
      }
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [ngoSearchTerm]);
  
  // Handle NGO selection
  const handleNgoSelect = (ngo) => {
    setSelectedNgo(ngo);
    setNgoSearchTerm('');
    setShowNgoSuggestions(false);
    setFilters({ ...filters, ngoId: ngo._id });
  };

  return (
    <>
      <SEOHead 
        title="NGO Blood Donation Projects India | Blood Donation Campaigns | BloodHero"
        description="NGO blood donation projects India - Browse and support blood donation campaigns across India. Find meaningful blood donation causes, NGO projects, and blood donation initiatives. Join the blood donation movement today!"
        keywords="NGO blood donation projects India, blood donation campaigns, NGO projects India, blood donation causes, support blood donation, NGO blood bank, blood donation initiatives, social blood donation, blood donation projects, blood donation campaigns India"
        canonicalUrl="/projects"
        ogImage="/images/ngo-projects-og.jpg"
        ogType="website"
        structuredData={projectsStructuredData}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50">
        {/* Hero Section with H1 */}
        <section className="relative py-20 bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <motion.h1 
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7 }}
                className="text-4xl md:text-6xl font-bold mb-6"
              >
                NGO Blood Donation
                <span className="block text-red-200">Projects</span>
              </motion.h1>
              
              <motion.p 
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="text-xl md:text-2xl text-red-100 mb-8 leading-relaxed"
              >
                Support meaningful blood donation campaigns and make a difference in communities across India
              </motion.p>
              
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              >
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
                  <Target className="w-5 h-5" />
                  <span className="font-medium">Active Projects</span>
                  <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                    {projects.length}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
                  <Heart className="w-5 h-5" />
                  <span className="font-medium">Lives Impacted</span>
                  <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                    1000+
                  </span>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Search and Filter Section with H2 */}
        <section className="py-12 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Find Your Perfect Blood Donation Project
                </h2>
                <p className="text-lg text-gray-600">
                  Search and filter through various NGO blood donation campaigns
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Search Projects */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  />
                </div>

                {/* NGO Search */}
                <div className="relative" ref={ngoSearchRef}>
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by NGO..."
                    value={ngoSearchTerm}
                    onChange={(e) => setNgoSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                  />
                  
                  {/* NGO Suggestions Dropdown */}
                  {showNgoSuggestions && (
                    <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto">
                      {loadingNgos ? (
                        <div className="p-4 text-center text-gray-500">Loading...</div>
                      ) : ngoSuggestions.length > 0 ? (
                        ngoSuggestions.map((ngo) => (
                          <button
                            key={ngo._id}
                            onClick={() => handleNgoSelect(ngo)}
                            className="w-full p-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors duration-200"
                          >
                            <div className="font-medium text-gray-900">{ngo.name}</div>
                            <div className="text-sm text-gray-500">{ngo.location}</div>
                          </button>
                        ))
                      ) : (
                        <div className="p-4 text-center text-gray-500">No NGOs found</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Filter Dropdown */}
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <select
                    value={filters.sortBy}
                    onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 appearance-none bg-white"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="urgent">Most Urgent</option>
                    <option value="popular">Most Popular</option>
                  </select>
                </div>
              </div>

              {/* Selected NGO Filter */}
              {selectedNgo && (
                <div className="mt-4 flex items-center gap-2">
                  <span className="text-sm text-gray-600">Filtered by:</span>
                  <div className="flex items-center gap-2 bg-red-100 text-red-700 px-3 py-1 rounded-full">
                    <span className="text-sm font-medium">{selectedNgo.name}</span>
                    <button
                      onClick={() => {
                        setSelectedNgo(null);
                        setFilters({ ...filters, ngoId: '' });
                      }}
                      className="text-red-500 hover:text-red-700 transition-colors duration-200"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Projects Grid Section with H2 */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Available Blood Donation Projects
                </h2>
                <p className="text-lg text-gray-600">
                  Browse through our curated list of NGO blood donation campaigns
                </p>
              </div>
              
              {loading ? (
                <div className="text-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading projects...</p>
                </div>
              ) : error ? (
                <div className="text-center py-20">
                  <div className="text-red-600 text-6xl mb-4">⚠️</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Projects</h3>
                  <p className="text-gray-600 mb-4">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                  >
                    Try Again
                  </button>
                </div>
              ) : projects.length === 0 ? (
                <div className="text-center py-20">
                  <div className="text-gray-400 text-6xl mb-4">📋</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Projects Found</h3>
                  <p className="text-gray-600">No blood donation projects match your current filters.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {projects
                    .filter(project => {
                      const matchesSearch = project.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                          project.description?.toLowerCase().includes(searchTerm.toLowerCase());
                      const matchesNGO = !filters.ngoId || project.ngo?._id === filters.ngoId;
                      return matchesSearch && matchesNGO;
                    })
                    .map((project, index) => (
                      <motion.div
                        key={project._id}
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
                      >
                        {/* Project Image */}
                        <div className="h-48 bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center">
                          {project.image ? (
                            <img 
                              src={project.image} 
                              alt={project.title} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="text-center">
                              <Heart className="w-16 h-16 text-red-400 mx-auto mb-2" />
                              <p className="text-red-600 font-medium">Blood Donation Project</p>
                            </div>
                          )}
                        </div>

                        {/* Project Content */}
                        <div className="p-6">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                              {project.category || 'Blood Donation'}
                            </span>
                            {project.isUrgent && (
                              <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">
                                Urgent
                              </span>
                            )}
                          </div>

                          <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                            {project.title || project.name}
                          </h3>

                          <p className="text-gray-600 mb-4 line-clamp-3">
                            {project.description}
                          </p>

                          {/* Project Stats */}
                          <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Calendar className="w-4 h-4" />
                              <span>{new Date(project.createdAt).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <MapPin className="w-4 h-4" />
                              <span>{project.location || 'India'}</span>
                            </div>
                          </div>

                          {/* NGO Info */}
                          {project.ngo && (
                            <div className="flex items-center gap-3 mb-6 p-3 bg-gray-50 rounded-lg">
                              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                <span className="text-red-600 font-bold text-sm">
                                  {project.ngo.name?.charAt(0) || 'N'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{project.ngo.name}</p>
                                <p className="text-sm text-gray-500">{project.ngo.location}</p>
                              </div>
                            </div>
                          )}

                          {/* Action Button */}
                          <Link
                            to={`/projects/${project._id}`}
                            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all duration-200 group"
                          >
                            <span>View Project</span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      </motion.div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Call to Action Section with H2 */}
        <section className="py-16 bg-gradient-to-r from-red-600 to-red-700 text-white">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Make a Difference?
              </h2>
              <p className="text-xl mb-8 text-red-100">
                Join our community of blood donors and help save lives across India
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register-donor"
                  className="px-8 py-4 bg-white text-red-600 font-bold rounded-xl hover:bg-gray-100 transition-all duration-200"
                >
                  Become a Donor
                </Link>
                <Link
                  to="/ngo-registration"
                  className="px-8 py-4 border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-red-600 transition-all duration-200"
                >
                  Register Your NGO
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProjectsPage;